// src/components/calendar/edit/Workshift.js
import { useContext } from "react";
import axiosInstance from "../../../config/axiosInstance";
import { enqueueSnackbar } from "notistack";
import { logApiError } from "../../../config/apiErrorHandling";
import { CalendarContext } from "../../context/CalendarContext";

const buildWorkChangeReplaceScopes = ({ ci, day, periodStart, includeFreedayPattern = false }) => {
  const scopes = [
    { kind: "employee_day", ci: String(ci), day },
    { kind: "day", day },
    { kind: "employee_period", ci: String(ci), category: "rest_between_days" },
  ];
  if (includeFreedayPattern) {
    scopes.push({ kind: "employee_period", ci: String(ci), category: "freeday_pattern" });
  }
  if (periodStart && day === periodStart) {
    scopes.push({ kind: "employee_period", ci: String(ci), category: "rotation_opening" });
  }
  return scopes;
};

/* === Cambiar turno de UN día (una sola request; 409 => conflicto) === */
export const useHandleChangeShift = () => {
  const { calendarData, setCalendarData, mergeCalendarValidationPatch } = useContext(CalendarContext);

  return async function changeShift(
    newShiftId, ci, day, currentShiftId, station_id, position_id, workarea_id,
    onStart = () => {}, onDone = () => {}
  ) {
    if (!ci || !day || !station_id || !position_id || !newShiftId) {
      enqueueSnackbar("Datos insuficientes", { variant: "error" }); onDone(null); return;
    }
    onStart();
    try {
      const { data } = await axiosInstance.post("/calendar/change_shift", {}, {
        params: {
          ci, day, station_id, position_id,
          new_shift_id: String(newShiftId),
          shift_id: currentShiftId ?? "",
          workarea_id: workarea_id ?? "",
          calendar_id: calendarData?._id ?? "",
        },
      });
      if (data?.status === 200 && data?.updated_calendar) {
        setCalendarData(data.updated_calendar);
        mergeCalendarValidationPatch({
          errors: data.errors,
          warnings: data.warnings,
          replaceScopes: buildWorkChangeReplaceScopes({
            ci,
            day,
            periodStart: calendarData?.start_date,
          }),
        });
        enqueueSnackbar("Turno cambiado", { variant: "success" });
        onDone({ ok: true, data });
        return { ok: true, data };
      } else {
        enqueueSnackbar(data?.message || "No se pudo cambiar el turno", { variant: "warning" });
        onDone({ ok: false, data });
        return { ok: false, data };
      }
    } catch (error) {
      const info = logApiError(
        "calendar.shift.change_day",
        error,
        "No se pudo cambiar el turno. Intenta nuevamente."
      );
      enqueueSnackbar(info.message, { variant: info.status === 403 || info.status === 409 ? "warning" : "error" });
      onDone({ ok: false, error, info });
      return { ok: false, error, info };
    }
  };
};

/* === Resolver conflicto multi-rol (mueve a S/A en origen) === */
export const useResolveMultiRole = () => {
  const { calendarData, setCalendarData, mergeCalendarValidationPatch } = useContext(CalendarContext);

  return async function resolveMultiRole(conflictPayload) {
    const params =
      conflictPayload?.suggested_action?.params
        ? conflictPayload.suggested_action.params
        : null;

    if (!params) {
      enqueueSnackbar("Faltan datos del conflicto", { variant: "error" });
      return { ok: false };
    }

    try {
      const { data } = await axiosInstance.post("/calendar/resolve_multi_role", {}, { params });

      const updated =
        data?.updated_calendar_to || data?.updated_calendar || data?.updated_calendar_from || null;

      if (updated) {
        setCalendarData(updated);
        const val = data?.to_validation || data?.from_validation || {};
        mergeCalendarValidationPatch({
          errors: val.errors,
          warnings: val.warnings,
          replaceScopes: buildWorkChangeReplaceScopes({
            ci: params.ci,
            day: params.date,
            periodStart: calendarData?.start_date,
          }),
        });
        enqueueSnackbar("Conflicto resuelto", { variant: "success" });
        return { ok: true, data };
      }

      enqueueSnackbar("Resuelto sin actualización", { variant: "warning" });
      return { ok: false, data };
    } catch (e) {
      const info = logApiError(
        "calendar.shift.resolve_multi_role",
        e,
        "No se pudo resolver el conflicto. Intenta nuevamente."
      );
      enqueueSnackbar(info.message, { variant: info.status === 403 || info.status === 409 ? "warning" : "error" });
      return { ok: false, error: e, info };
    }
  };
};

/* === Cambiar turno de toda la quincena (una sola request) === */
export const useHandleChangeShiftPeriod = () => {
  const { setCalendarData, replaceCalendarValidation } = useContext(CalendarContext);

  return async function changeShiftPeriod(
    id_workshift,
    ci,
    day,
    station_id,
    position_id,
    setLoading = () => {},
    onFinish = () => {}
  ) {
    if (!ci || !day || !station_id || !position_id || !id_workshift) {
      enqueueSnackbar("Datos insuficientes", { variant: "error" });
      onFinish();
      return;
    }

    try {
      setLoading(true);
      const { data } = await axiosInstance.post(
        "/calendar/assign_initial_workshift",
        {},
        {
          params: {
            ci,
            day,
            id_workshift,
            station_id,
            position_id,
          },
        }
      );

      if (data?.status === 200 && data?.updated_calendar) {
        enqueueSnackbar("Turno aplicado a la quincena", { variant: "success" });
        setCalendarData(data.updated_calendar);
        replaceCalendarValidation({
          errors: data.errors,
          warnings: data.warnings,
        });
        return { ok: true, data };
      } else {
        enqueueSnackbar(data?.message || "No se pudo aplicar el turno", { variant: "warning" });
        return { ok: false, data };
      }
    } catch (error) {
      const info = logApiError(
        "calendar.shift.change_period",
        error,
        "No se pudo aplicar el turno a la quincena. Intenta nuevamente."
      );
      enqueueSnackbar(info.message, { variant: info.status === 403 || info.status === 409 ? "warning" : "error" });
      return { ok: false, error, info };
    } finally {
      setLoading(false);
      onFinish();
    }
  };
};
